import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  ArrowLeft, Upload, Send, AlertCircle, 
  ArrowDown, ArrowUp, CircleAlert, ChevronRight, ChevronDown,
  Lock, MessageSquare, Clock, Paperclip, Download, Eye,
  User as UserIcon, CheckCircle2, Loader2, FileText, Image,
  RotateCcw, X, BookOpen, ExternalLink
} from 'lucide-react';
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { cn } from "../../components/ui/utils";
import { 
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator 
} from "../../components/ui/breadcrumb";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import { serviceDeskApi } from "../../utils/service-desk-api";
import api from "../../utils/api";
import { toast } from "sonner";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import { useAuth } from "../../context/AuthContext";
import { ResolutionDetailsModal } from "../../components/service-desk/ResolutionDetailsModal";

const STATUS_CONFIG: Record<string, { label: string; color: string; description: string }> = {
  NEW: { 
    label: "New Request", 
    color: "bg-blue-100 text-blue-700",
    description: "Initial state when request is first submitted and awaiting team review."
  },
  TODO: { 
    label: "To Do", 
    color: "bg-gray-100 text-gray-700",
    description: "Request has been reviewed and added to the work queue."
  },
  DOING: { 
    label: "Doing", 
    color: "bg-orange-100 text-orange-700",
    description: "Work is currently in progress for this request."
  },
  BLOCKED: { 
    label: "Blocked", 
    color: "bg-red-100 text-red-700",
    description: "Progress is halted due to external dependencies or missing information."
  },
  RESOLVED: { 
    label: "Resolved", 
    color: "bg-green-100 text-green-700",
    description: "The issue has been addressed and is awaiting requester confirmation."
  },
  CLOSED: { 
    label: "Closed", 
    color: "bg-gray-200 text-gray-600",
    description: "Request is finalized and no further action is required."
  },
};

const PRIORITY_CONFIG: Record<string, { label: string; color: string; icon: any }> = {
  URGENT: { label: "Urgent", color: "bg-red-100 text-red-700", icon: ArrowUp },
  HIGH: { label: "High", color: "bg-orange-100 text-orange-700", icon: ArrowUp },
  MEDIUM: { label: "Medium", color: "bg-blue-100 text-blue-700", icon: ArrowDown },
  LOW: { label: "Low", color: "bg-gray-100 text-gray-600", icon: ArrowDown },
};

export default function RequestView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const ability = useAbility(AbilityContext);
  const { currentUser } = useAuth();
  
  const [request, setRequest] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [comment, setComment] = useState("");
  const [isInternal, setIsInternal] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isAssigneeModalOpen, setIsAssigneeModalOpen] = useState(false);
  const [isResolutionModalOpen, setIsResolutionModalOpen] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [selectedAssignees, setSelectedAssignees] = useState<string[]>([]);
  const [isUpdatingAssignee, setIsUpdatingAssignee] = useState(false);
  const [isFieldsExpanded, setIsFieldsExpanded] = useState(false);
  const [isProceduresExpanded, setIsProceduresExpanded] = useState(false);
  const [resolvedLabels, setResolvedLabels] = useState<Record<string, string>>({});
  const [isResolvingLabels, setIsResolvedLabels] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Resolve Idarah Data IDs to readable labels
  useEffect(() => {
    const resolveLabels = async () => {
      if (!request || !request.formVersion?.fields || !request.data) return;
      
      const dataFields = request.formVersion.fields.filter((f: any) => f.type === 'idarah_data');
      if (dataFields.length === 0) return;

      setIsResolvedLabels(true);
      const newLabels: Record<string, string> = {};

      try {
        for (const field of dataFields) {
          const value = request.data[field.id];
          if (!value) continue;

          const ids = Array.isArray(value) ? value : [value];
          if (ids.length === 0) continue;

          // Fetch source data to get labels
          const response = await api.get(`/api/service-desk/reference-data/${field.dataSource}`);
          if (response.success && response.data) {
            ids.forEach(id => {
              const match = response.data.find((item: any) => String(item.id) === String(id));
              if (match) {
                const label = match.name || match.roleName || match.procedureName || match.trainingPlanName || match.projectName || match.tradingName || match.legalName || match.title || "Unknown";
                newLabels[String(id)] = label;
              }
            });
          }
        }
        setResolvedLabels(prev => ({ ...prev, ...newLabels }));
      } catch (err) {
        console.error("Error resolving field labels:", err);
      } finally {
        setIsResolvedLabels(false);
      }
    };

    resolveLabels();
  }, [request]);

  // Handle auto-reopen from email link
  useEffect(() => {
    if (request && !isLoading && !isUpdatingStatus) {
      const searchParams = new URLSearchParams(location.search);
      const action = searchParams.get('action');
      const canCurrentlyReopen = (request.status === 'RESOLVED' || request.status === 'CLOSED') && 
                                 !request.formVersion?.requestForm?.serviceDesk?.isRetired &&
                                 String(request.requester?.id) === String(currentUser?.id);

      if (action === 'reopen' && canCurrentlyReopen) {
        handleStatusChange('DOING');
        // Clear the query param to avoid repeated reopens
        const newPath = location.pathname;
        navigate(newPath, { replace: true });
      }
    }
  }, [location.search, request, isLoading, currentUser, navigate]);
  
  // Mentions State
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionIndex, setMentionIndex] = useState(-1);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const mentionableUsers = useMemo(() => {
    if (!request) return [];
    const usersMap = new Map();

    // Add requester (only for public comments)
    if (request.requester && !isInternal) {
      usersMap.set(request.requester.id, request.requester);
    }

    // Add team members
    const teamMembers = request.formVersion?.requestForm?.serviceDesk?.members || [];
    teamMembers.forEach((member: any) => {
      if (member.user && !member.user.archived) {
        // If private comment, ensure we don't accidentally add the requester
        if (isInternal && String(member.user.id) === String(request.requester?.id)) return;
        usersMap.set(member.user.id, member.user);
      }
    });

    // Add assignees
    const assignees = request.assignees || [];
    assignees.forEach((assignee: any) => {
        // If private comment, ensure we don't accidentally add the requester
        if (isInternal && String(assignee.id) === String(request.requester?.id)) return;
        usersMap.set(assignee.id, assignee);
    });

    return Array.from(usersMap.values()).filter((u: any) => String(u.id) !== String(currentUser?.id));
  }, [request, currentUser, isInternal]);

  const filteredMentions = useMemo(() => {
    return mentionableUsers.filter(u => 
      u.name?.toLowerCase().includes(mentionQuery.toLowerCase())
    );
  }, [mentionableUsers, mentionQuery]);

  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setComment(value);

    const cursorPosition = e.target.selectionStart || 0;
    const textBeforeCursor = value.slice(0, cursorPosition);
    
    // Match the last word before the cursor starting with @
    const match = textBeforeCursor.match(/(?:^|\s)@(\w*)$/);

    if (match) {
      setMentionQuery(match[1]);
      // Match index gives the start of the match (which might include a space).
      // We want the exact index of the '@' symbol.
      const atSymbolIndex = match.index! + match[0].indexOf('@');
      setMentionIndex(atSymbolIndex);
      setShowMentions(true);
    } else {
      setShowMentions(false);
    }
  };

  const handleSelectMention = (user: any) => {
    const beforeMention = comment.slice(0, mentionIndex);
    const afterMention = comment.slice(mentionIndex + mentionQuery.length + 1);
    
    const newComment = `${beforeMention}@${user.name} ${afterMention}`;
    setComment(newComment);
    setShowMentions(false);
    
    // Focus back to input
    if (inputRef.current) {
        inputRef.current.focus();
    }
  };

  const renderCommentWithMentions = (text: string) => {
    if (!text || !mentionableUsers.length) return text;
    
    // Sort users by name length descending to match longest names first (greedy match)
    const sortedUsers = [...mentionableUsers]
      .filter(u => u.name)
      .sort((a, b) => (b.name.length - a.name.length));

    // Escape names for regex and join with |
    const userNamesPattern = sortedUsers
      .map(u => u.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
      .join('|');

    if (!userNamesPattern) return text;

    // Create a regex that matches @ followed by one of the specific user names.
    // We use a capture group so split() keeps the matched part.
    const regex = new RegExp(`(@(?:${userNamesPattern}))`, 'g');
    const parts = text.split(regex);
    
    return parts.map((part, i) => {
      if (part.startsWith('@')) {
        const potentialName = part.slice(1);
        const isRealUser = sortedUsers.some(u => u.name === potentialName);
        if (isRealUser) {
          return <span key={i} className="font-bold text-primary">{part}</span>;
        }
      }
      return part;
    });
  };

  const fetchDetails = async () => {
    if (!id) return;
    try {
      const response = await serviceDeskApi.getRequestDetails(id);
      if (response.success) {
        setRequest(response.request);
      } else {
        toast.error("Failed to load request details.");
        navigate('/my-requests');
      }
    } catch (error) {
      console.error("Error fetching request details:", error);
      toast.error("An error occurred while loading request details.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails();
  }, [id, navigate]);

  // Sync selection when modal opens
  useEffect(() => {
    if (isAssigneeModalOpen && request?.assignees) {
      setSelectedAssignees(request.assignees.map((u: any) => u.id.toString()));
    }
  }, [isAssigneeModalOpen, request]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !id) return;

    setIsUploading(true);
    try {
        for (const file of Array.from(files)) {
            const uploadFormData = new FormData();
            uploadFormData.append('image', file);
            uploadFormData.append('context', 'service-desk');

            const uploadResult = await api.post('/api/upload/s3', uploadFormData, { skipErrorToast: true });
            if (uploadResult.url) {
                const response = await serviceDeskApi.addAttachment(id, {
                    filename: file.name,
                    url: uploadResult.url,
                    size: uploadResult.size || file.size,
                    key: uploadResult.key
                });
                if (response.success) {
                    setRequest((prev: any) => ({
                        ...prev,
                        attachments: [...(prev.attachments || []), response.attachment]
                    }));
                }
            }
        }
        toast.success("File(s) uploaded successfully.");
    } catch (error) {
        console.error("Upload error:", error);
        toast.error("Failed to upload file(s).");
    } finally {
        setIsUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSendComment = async () => {
    if (!comment.trim() || !id) return;
    
    setIsSending(true);
    try {
        const response = await serviceDeskApi.addComment(id, { 
            text: comment,
            isPrivate: isInternal
        });
        if (response.success) {
            setComment("");
            setIsInternal(false);
            setRequest((prev: any) => ({
                ...prev,
                comments: [...(prev.comments || []), response.comment]
            }));
            toast.success("Comment added.");
        }
    } catch (error) {
        console.error("Error sending comment:", error);
        toast.error("Failed to add comment.");
    } finally {
        setIsSending(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!id || newStatus === request?.status) return;
    
    if (newStatus === 'RESOLVED') {
      setIsResolutionModalOpen(true);
      return;
    }

    setIsUpdatingStatus(true);
    try {
        const response = await serviceDeskApi.updateStatus(id, newStatus);
        if (response.success) {
            setRequest((prev: any) => ({ ...prev, status: newStatus }));
            toast.success(`Status updated to ${STATUS_CONFIG[newStatus].label}`);
        }
    } catch (error) {
        toast.error("Failed to update status.");
    } finally {
        setIsUpdatingStatus(false);
    }
  };

  const handleResolutionSubmit = async (details: any) => {
    if (!id) return;
    
    setIsUpdatingStatus(true);
    try {
      const response = await serviceDeskApi.updateStatus(id, 'RESOLVED', undefined, details);
      if (response.success) {
        setRequest((prev: any) => ({ 
          ...prev, 
          status: 'RESOLVED',
          resolutionIssue: details.issue,
          resolutionRootCause: details.rootCause,
          resolutionSolution: details.solution,
          resolutionNextSteps: details.nextSteps
        }));
        setIsResolutionModalOpen(false);
        toast.success("Ticket resolved successfully");
      }
    } catch (error) {
      toast.error("Failed to resolve ticket");
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleSaveAssignees = async () => {
    if (!id) return;
    setIsUpdatingAssignee(true);
    try {
        const response = await serviceDeskApi.updateAssignees(id, selectedAssignees);
        if (response.success) {
            await fetchDetails();
            setIsAssigneeModalOpen(false);
            toast.success("Assignees updated successfully.");
        }
    } catch (error) {
        toast.error("Failed to update assignees.");
    } finally {
        setIsUpdatingAssignee(false);
    }
  };

  const toggleAssignee = (userId: string) => {
    setSelectedAssignees(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId) 
        : [...prev, userId]
    );
  };

  const handleDownload = async (attachmentId: string, filename: string) => {
    try {
        const response = await serviceDeskApi.getAttachmentDownloadUrl(attachmentId);
        if (response.success && response.url) {
            const link = document.createElement('a');
            link.href = response.url;
            link.setAttribute('download', filename);
            link.setAttribute('target', '_blank');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else {
            toast.error("Failed to generate download link.");
        }
    } catch (error) {
        console.error("Download error:", error);
        toast.error("Error downloading file.");
    }
  };

  // Whether a file is an image — only these get the View CTA that previews the
  // file in a new browser tab (inline signed URL). Mirrors getFileIcon()'s set.
  const isImageFile = (filename: string) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'heic'].includes(ext || '');
  };

  const handleViewAttachment = async (attachmentId: string) => {
    try {
        const response = await serviceDeskApi.getAttachmentDownloadUrl(attachmentId, 'view');
        if (response.success && response.url) {
            window.open(response.url, '_blank', 'noopener,noreferrer');
        } else {
            toast.error("Failed to generate preview link.");
        }
    } catch (error) {
        console.error("Preview error:", error);
        toast.error("Error previewing file.");
    }
  };

  const getFileIcon = (filename: string) => {
    if (!filename) return <Paperclip className="w-4 h-4 text-gray-400 group-hover:text-blue-500 transition-colors shrink-0" />;
    
    const ext = filename.split('.').pop()?.toLowerCase();
    
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'heic'].includes(ext || '')) {
        return <Image className="w-4 h-4 text-blue-500 group-hover:text-blue-600 transition-colors shrink-0" />;
    }
    
    if (ext === 'pdf') {
        return <FileText className="w-4 h-4 text-red-500 group-hover:text-red-600 transition-colors shrink-0" />;
    }
    
    if (['doc', 'docx', 'txt', 'rtf'].includes(ext || '')) {
        return <FileText className="w-4 h-4 text-blue-600 group-hover:text-blue-700 transition-colors shrink-0" />;
    }
    
    if (['xls', 'xlsx', 'csv', 'ods'].includes(ext || '')) {
        return <FileText className="w-4 h-4 text-green-600 group-hover:text-green-700 transition-colors shrink-0" />;
    }

    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext || '')) {
        return <FileText className="w-4 h-4 text-orange-600 group-hover:text-orange-700 transition-colors shrink-0" />;
    }
    
    // Generic file fallback
    return <FileText className="w-4 h-4 text-gray-400 group-hover:text-blue-500 transition-colors shrink-0" />;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-200px)]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!request) return null;

  // Calculate SLA Breach
  const createdAtDate = new Date(request.createdAt);
  const now = new Date();
  const diffHours = (now.getTime() - createdAtDate.getTime()) / (1000 * 60 * 60);
  const slaHours = request.formVersion?.requestForm?.serviceDesk?.slaHours || 48;
  const isBreached = diffHours > slaHours && !['RESOLVED', 'CLOSED'].includes(request.status);

  const PriorityIcon = PRIORITY_CONFIG[request.priority]?.icon || ArrowDown;
  const formattedId = `REQ-${request.id.toString().padStart(3, '0')}`;
  const isRetired = request.formVersion?.requestForm?.serviceDesk?.isRetired;
  const teamMembers = request.formVersion?.requestForm?.serviceDesk?.members || [];

  const currentMember = teamMembers.find((m: any) => String(m.user?.id) === String(currentUser?.id));
  const currentMemberRole = currentMember?.role; // 'TEAM_ADMIN', 'EDITOR', 'VIEWER'
  
  const isSystemAdmin = ability.can(ACTIONS.MANAGE, RESOURCES.SERVICE_DESK_ADMIN);
  const isSystemAgent = ability.can(ACTIONS.MANAGE, RESOURCES.SERVICE_DESK_AGENT);
  
  const isDeskEditor = isSystemAdmin || currentMemberRole === 'TEAM_ADMIN' || currentMemberRole === 'EDITOR';
  const isDeskViewer = isDeskEditor || isSystemAgent || currentMemberRole === 'VIEWER';
  const isRequester = String(request.requester?.id) === String(currentUser?.id);

  const canManageStatus = isDeskEditor && !isRetired;
  const canReopen = (request.status === 'RESOLVED' || request.status === 'CLOSED') && 
                    !isRetired && isRequester;
  
  const canChangeAssignee = (currentMemberRole === 'TEAM_ADMIN') && !isRetired;
  
  const canPrivateComment = isDeskEditor;
  const canViewPrivateComments = isDeskViewer;
  
  const canUpload = !isRetired && (isRequester || isDeskEditor);
  const canPublicComment = !isRetired && (isRequester || isDeskEditor);

  const publicComments = (request.comments || [])
    .filter((c: any) => !c.isPrivate)
    .sort((a: any, b: any) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    
  const internalComments = (request.comments || [])
    .filter((c: any) => c.isPrivate)
    .sort((a: any, b: any) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

  // --- Filtering Logic for Supplemental Attachments ---
  // We want to avoid showing files in the bottom list if they are already visible in the "Form Data" section
  const formFieldUrls = new Set<string>();
  if (request.data) {
      Object.values(request.data).forEach((value: any) => {
          if (Array.isArray(value)) {
              value.forEach((item: any) => {
                  if (item && typeof item === 'object' && item.url) {
                      formFieldUrls.add(item.url);
                  }
              });
          }
      });
  }

  const supplementalAttachments = (request.attachments || []).filter((att: any) => !formFieldUrls.has(att.url));

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-white -m-6 h-[calc(100dvh-64px)]">
      {/* Header Section */}
      <div className="p-6 border-b border-border">
        <div className="flex items-start gap-4">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-accent rounded-lg transition-colors mt-1"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          
          <div className="flex-1">
            <div className="mb-2">
              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem>
                    <BreadcrumbPage className="font-bold">{formattedId}</BreadcrumbPage>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator>•</BreadcrumbSeparator>
                  <BreadcrumbItem>
                    <BreadcrumbPage>{request.title}</BreadcrumbPage>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator>•</BreadcrumbSeparator>
                  <BreadcrumbItem>
                    <BreadcrumbLink asChild>
                      <Link to={`/service-desk/${request.formVersion?.requestForm?.serviceDesk?.id}`} className="font-medium">
                        {request.formVersion?.requestForm?.serviceDesk?.name || "Service Desk"}
                        {isRetired && <span className="ml-1 text-gray-400 italic">(retired)</span>}
                      </Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
            </div>
            <h1 className="text-2xl font-bold mb-3">{request.title}</h1>
            <div className="flex items-center gap-3">
              <span className={cn("inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold uppercase", STATUS_CONFIG[request.status]?.color)}>
                {STATUS_CONFIG[request.status]?.label}
              </span>
              {canReopen && (
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="h-7 text-xs gap-1 border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:text-blue-800 font-bold uppercase"
                  onClick={() => handleStatusChange('DOING')}
                  disabled={isUpdatingStatus}
                >
                  {isUpdatingStatus ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                  Reopen Ticket
                </Button>
              )}
              <span className={cn("inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-bold uppercase", PRIORITY_CONFIG[request.priority]?.color)}>
                <PriorityIcon className="w-3 h-3" />
                {PRIORITY_CONFIG[request.priority]?.label}
              </span>
              {isBreached && (
                <div className="flex items-center gap-1 px-2.5 py-1 bg-red-50 text-red-700 rounded-md border border-red-200 text-xs font-bold uppercase">
                    <CircleAlert className="w-3 h-3" />
                    SLA Breached
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Description */}
          <div>
            <h3 className="text-lg font-bold mb-2">Description</h3>
            <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
              {request.data?.description || "No description provided."}
            </p>
          </div>

          {/* Procedures Section - For Staff Only */}
          {isDeskViewer && request.formVersion?.linkedProcedures?.length > 0 && (
            <div className="border border-blue-100 rounded-xl overflow-hidden shadow-sm bg-blue-50/30">
                <button 
                    onClick={() => setIsProceduresExpanded(!isProceduresExpanded)}
                    className="w-full flex items-center justify-between p-4 bg-blue-50/50 hover:bg-blue-100/50 transition-colors border-b border-blue-100"
                >
                    <div className="flex items-center gap-2">
                        <BookOpen className="w-5 h-5 text-blue-600" />
                        <span className="font-bold text-blue-900 uppercase tracking-tight text-sm">Linked Procedures</span>
                    </div>
                    <ChevronDown className={cn("w-4 h-4 text-blue-400 transition-transform duration-200", isProceduresExpanded ? "" : "-rotate-90")} />
                </button>
                
                {isProceduresExpanded && (
                    <div className="p-4 bg-white animate-in fade-in slide-in-from-top-1 duration-200">
                        <div className="space-y-1">
                            {request.formVersion.linkedProcedures.map((proc: any) => (
                                <a 
                                    key={proc.id}
                                    href={`/procedure/${proc.id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-between p-3 rounded-xl hover:bg-blue-50/50 group transition-all border border-transparent hover:border-blue-100"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 rounded-lg bg-blue-50 text-blue-600 group-hover:bg-blue-100 transition-colors">
                                            <FileText className="w-4 h-4" />
                                        </div>
                                        <span className="text-sm font-bold text-gray-700 group-hover:text-blue-700 transition-colors">
                                            {proc.procedureName}
                                        </span>
                                    </div>
                                    <ExternalLink className="w-4 h-4 text-gray-300 group-hover:text-blue-500 transition-colors" />
                                </a>
                            ))}
                        </div>
                    </div>
                )}
            </div>
          )}

          {/* Dynamic Form Fields */}
          {request.formVersion?.fields?.length > 0 && (
            <div className="border border-border rounded-xl overflow-hidden shadow-sm">
                <button 
                    onClick={() => setIsFieldsExpanded(!isFieldsExpanded)}
                    className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors border-b border-border/50"
                >
                    <div className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-gray-400" />
                        <span className="font-bold text-gray-900 uppercase tracking-tight text-sm">Form Data</span>
                    </div>
                    <ChevronDown className={cn("w-4 h-4 text-gray-400 transition-transform duration-200", isFieldsExpanded ? "" : "-rotate-90")} />
                </button>
                
                {isFieldsExpanded && (
                    <div className="p-6 bg-white animate-in fade-in slide-in-from-top-1 duration-200">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            {request.formVersion.fields.map((field: any) => {
                                if (['heading', 'static_text'].includes(field.type)) return null;
                                const value = request.data?.[field.id];
                                if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) return null;
                                
                                return (
                                    <div key={field.id} className={cn("space-y-1", field.type === 'file' ? "col-span-2" : "")}>
                                        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest">{field.label}</label>
                                        <div className="text-sm font-medium text-gray-900">
                                            {field.type === 'file' ? (
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                                                    {(Array.isArray(value) ? value : value ? [value] : []).map((file: any, idx: number) => {
                                                        const matchingAttachment = request.attachments?.find((a: any) => 
                                                            (a.key && file.key && a.key === file.key) || (a.url === file.url)
                                                        );
                                                        
                                                        return (
                                                            <div key={idx} className="flex items-center justify-between p-3 rounded-xl border border-gray-100 bg-white shadow-sm group hover:border-primary/20 transition-all">
                                                                <div className="flex items-center min-w-0">
                                                                    <div className="p-2 rounded-lg bg-gray-50 mr-3 border border-gray-100">
                                                                        {getFileIcon(file.name)}
                                                                    </div>
                                                                    <div className="flex flex-col min-w-0">
                                                                        <span className="text-sm font-bold text-gray-700 truncate" title={file.name}>{file.name}</span>
                                                                        <span className="text-[10px] text-gray-400 font-bold uppercase tracking-tight">
                                                                            {file.size ? `${(Number(file.size) / 1024 / 1024).toFixed(2)} MB` : "File Attachment"}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-center gap-1 shrink-0">
                                                                    {isImageFile(file.name) && (
                                                                        <button 
                                                                            type="button"
                                                                            onClick={() => {
                                                                                if (matchingAttachment) {
                                                                                    handleViewAttachment(matchingAttachment.id);
                                                                                } else {
                                                                                    window.open(file.url, '_blank');
                                                                                }
                                                                            }}
                                                                            className="p-1.5 text-gray-400 hover:text-primary transition-colors"
                                                                            title="View file"
                                                                        >
                                                                            <Eye className="w-4 h-4" />
                                                                        </button>
                                                                    )}
                                                                    <button 
                                                                        type="button"
                                                                        onClick={() => {
                                                                            if (matchingAttachment) {
                                                                                handleDownload(matchingAttachment.id, matchingAttachment.name);
                                                                            } else {
                                                                                window.open(file.url, '_blank');
                                                                            }
                                                                        }}
                                                                        className="p-1.5 text-gray-400 hover:text-primary transition-colors"
                                                                        title="Download file"
                                                                    >
                                                                        <Download className="w-4 h-4" />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            ) : field.type === 'idarah_data' ? (
                                                <div className="flex flex-wrap gap-1 mt-1">
                                                    {(Array.isArray(value) ? value : [value]).map((id: any, i: number) => (
                                                        <Badge 
                                                            key={i} 
                                                            variant="secondary" 
                                                            className="bg-gray-100 text-black border-gray-200 font-bold text-[10px] px-2 py-0.5"
                                                        >
                                                            {resolvedLabels[String(id)] || (isResolvingLabels ? "Loading..." : `ID: ${id}`)}
                                                        </Badge>
                                                    ))}
                                                </div>
                                            ) : (
                                                Array.isArray(value) ? value.join(', ') : String(value)
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
          )}

          {/* Metadata Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-12 pt-4">
            <div>
              <h4 className="text-sm font-bold mb-3 uppercase tracking-wider text-gray-400">Submitter</h4>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 text-sm bg-pink-500 rounded-full flex items-center justify-center text-white font-bold shadow-sm" title={request.requester?.name}>
                  {request.requester?.name?.split(' ').map((n: any) => n[0]).join('') || "U"}
                </div>
                <div>
                  <div className="text-sm font-bold text-gray-900">{request.requester?.name}</div>
                  <div className="text-xs text-muted-foreground">{request.requester?.email}</div>
                </div>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-bold uppercase tracking-wider text-gray-400">Assigned To</h4>
                {canChangeAssignee && (
                  <button 
                    onClick={() => setIsAssigneeModalOpen(true)}
                    className="text-xs font-bold text-primary hover:underline uppercase tracking-tight"
                  >
                    Change
                  </button>
                )}
              </div>
              <div className="space-y-3">
                {request.assignees && request.assignees.length > 0 ? (
                    request.assignees.map((assignee: any) => (
                        <div key={assignee.id} className="flex items-center gap-2">
                            <div className="w-8 h-8 text-sm bg-purple-600 rounded-full flex items-center justify-center text-white font-bold shadow-sm" title={assignee.name}>
                                {assignee.name.split(' ').map((n: any) => n[0]).join('')}
                            </div>
                            <div>
                                <div className="text-sm font-bold text-gray-900">{assignee.name}</div>
                                <div className="text-xs text-muted-foreground">{assignee.email}</div>
                            </div>
                        </div>
                    ))
                ) : (
                    <span className="text-sm text-gray-400 italic font-medium">Awaiting Assignment</span>
                )}
              </div>
            </div>

            <div>
              <h4 className="text-sm font-bold mb-1 uppercase tracking-wider text-gray-400">Created</h4>
              <p className="text-sm text-muted-foreground font-medium">
                {new Date(request.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}
              </p>
            </div>

            <div>
              <h4 className="text-sm font-bold mb-1 uppercase tracking-wider text-gray-400">Last Updated</h4>
              <p className="text-sm text-muted-foreground font-medium">
                {new Date(request.updatedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}
              </p>
            </div>
          </div>

          {/* Status Management - Agent Only */}
          {canManageStatus && (
            <div className="bg-card border border-border rounded-xl p-6 shadow-sm">
                <div className="flex items-center justify-between mb-6">
                    <h4 className="font-bold text-gray-900">Status Management</h4>
                    <span className={cn("inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold uppercase", STATUS_CONFIG[request.status]?.color)}>
                        {STATUS_CONFIG[request.status]?.label}
                    </span>
                </div>
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-bold text-gray-400 uppercase tracking-wider mb-2">Update Status</label>
                        <div className="relative">
                            <select 
                                className="w-full h-11 px-4 py-2 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 text-sm font-medium appearance-none cursor-pointer pr-10"
                                value={request.status}
                                onChange={(e) => handleStatusChange(e.target.value)}
                                disabled={isUpdatingStatus}
                            >
                                {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                                    <option key={key} value={key}>{config.label}</option>
                                ))}
                            </select>
                            <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none">
                                <ChevronDown className="h-4 w-4 text-gray-400" />
                            </div>
                        </div>
                    </div>
                    <div className="bg-gray-50 border border-gray-100 rounded-xl p-4">
                        <div className="space-y-1">
                            <div className="flex items-center gap-2">
                                <div className={cn("w-2 h-2 rounded-full", STATUS_CONFIG[request.status]?.color.split(' ')[1].replace('text-', 'bg-'))}></div>
                                <p className="text-sm font-bold text-gray-900">{STATUS_CONFIG[request.status]?.label}</p>
                            </div>
                            <p className="text-xs text-muted-foreground pl-4 leading-relaxed font-medium">
                                {STATUS_CONFIG[request.status]?.description}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
          )}

          {/* Attachments */}
          <div>
            <h4 className="text-sm font-bold mb-3 uppercase tracking-wider text-gray-400">Add Attachments</h4>
            {canUpload && (
              <div className="space-y-2 mb-4">
                <label className={cn(
                  "flex items-center gap-2 px-4 py-3 border border-dashed border-border rounded-xl hover:bg-accent cursor-pointer transition-colors group",
                  isUploading && "opacity-50 pointer-events-none"
                )}>
                  {isUploading ? (
                    <Loader2 className="w-4 h-4 animate-spin text-primary" />
                  ) : (
                    <Upload className="w-4 h-4 text-gray-400 group-hover:text-primary transition-colors" />
                  )}
                  <span className="text-sm font-semibold text-gray-600 group-hover:text-primary transition-colors">
                    {isUploading ? "Uploading..." : "Upload Files (Max 10MB each)"}
                  </span>
                  <input 
                    type="file" 
                    multiple 
                    className="hidden" 
                    onChange={handleFileUpload}
                    ref={fileInputRef}
                    disabled={isUploading}
                  />
                </label>
              </div>
            )}
            
            {!canUpload && !isRetired && (
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl text-center text-xs text-gray-500 font-medium mb-4 italic">
                    You do not have permission to upload attachments to this request.
                </div>
            )}

            <h4 className="text-sm font-bold mb-3 uppercase tracking-wider text-gray-400">Attachments</h4>
            {supplementalAttachments && supplementalAttachments.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {supplementalAttachments.map((file: any) => (
                   <div key={file.id} className="flex items-center justify-between p-3 rounded-xl border border-gray-100 bg-white shadow-sm group hover:border-primary/20 transition-all">
                      <div className="flex items-center min-w-0">
                         <div className="p-2 rounded-lg bg-gray-50 mr-3 border border-gray-100">
                            {getFileIcon(file.name)}
                         </div>
                         <div className="flex flex-col min-w-0">
                            <span className="text-sm font-bold text-gray-700 truncate" title={file.name}>{file.name}</span>
                            <span className="text-[10px] text-gray-400 font-bold uppercase tracking-tight">
                                {file.size ? `${(Number(file.size) / 1024 / 1024).toFixed(2)} MB` : "File Attachment"}
                            </span>
                         </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {isImageFile(file.name) && (
                          <button 
                            type="button"
                            onClick={() => handleViewAttachment(file.id)}
                            className="p-1.5 text-gray-400 hover:text-primary transition-colors"
                            title="View file"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        )}
                        <button 
                          type="button"
                          onClick={() => handleDownload(file.id, file.name)}
                          className="p-1.5 text-gray-400 hover:text-primary transition-colors"
                          title="Download file"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      </div>
                   </div>
                ))}
              </div>
            ) : (
                <div className="p-4 border border-dashed border-gray-200 rounded-xl text-center text-sm text-gray-400 font-medium">
                    No attachments
                </div>
            )}
          </div>

          {/* Internal Comments - Agent Only */}
          {canViewPrivateComments && (
            <div>
                <h3 className="text-lg font-bold mb-1 flex items-center gap-2">
                    <Lock className="w-5 h-5 text-amber-600" />
                    Internal Comments
                </h3>
                <p className="text-xs text-muted-foreground mb-4 font-medium">Private notes visible only to team members - hidden from the requester</p>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 shadow-sm">
                    <div className="space-y-6">
                        {internalComments.length > 0 ? (
                            internalComments.map((c: any) => (
                                <div key={c.id} className="flex gap-3">
                                    <div className="w-8 h-8 text-xs bg-amber-600 rounded-full flex items-center justify-center text-white font-bold shadow-sm">
                                        {c.author?.name?.split(' ').map((n: any) => n[0]).join('') || "A"}
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-baseline gap-2 mb-1 flex-wrap">
                                            <span className="text-sm font-bold text-gray-900">{c.author?.name}</span>
                                            {c.author?.archived && (
                                                <Badge variant="outline" className="text-[9px] py-0 h-4 bg-gray-100 text-gray-600 border-gray-300 font-bold uppercase tracking-wider">
                                                    Archived
                                                </Badge>
                                            )}
                                            {!c.author?.isActive && !c.author?.archived && (
                                                <Badge variant="outline" className="text-[9px] py-0 h-4 bg-red-50 text-red-600 border-red-200 font-bold uppercase tracking-wider">
                                                    Inactive
                                                </Badge>
                                            )}
                                            <span className="text-[10px] font-bold text-amber-600/70 uppercase">
                                                {new Date(c.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, {new Date(c.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </span>
                                        </div>
                                        <div className="text-sm text-amber-900 font-medium leading-relaxed whitespace-pre-wrap">
                                            {renderCommentWithMentions(c.text)}
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p className="text-sm text-amber-600/70 text-center py-4 font-bold uppercase tracking-wider">No internal comments yet</p>
                        )}
                    </div>
                </div>
            </div>
          )}

          {/* Public Comments */}
          <div>
            <h3 className="text-lg font-bold mb-1">Public Comments</h3>
            <p className="text-xs text-muted-foreground mb-4 font-medium">Visible to the requester and anyone with access to this request</p>
            <div className="bg-card border border-border rounded-xl p-6 shadow-sm">
              <div className="space-y-6">
                {publicComments.length > 0 ? (
                  publicComments.map((c: any) => (
                    <div key={c.id} className="flex gap-3">
                       <div className={cn("w-8 h-8 text-xs rounded-full flex items-center justify-center text-white font-bold shadow-sm", c.author?.id === request.requester?.id ? "bg-pink-500" : "bg-blue-600")}>
                          {c.author?.name?.split(' ').map((n: any) => n[0]).join('') || "U"}
                       </div>
                       <div className="flex-1">
                          <div className="flex items-baseline gap-2 mb-1 flex-wrap">
                            <span className="text-sm font-bold text-gray-900">{c.author?.name}</span>
                            {c.author?.archived && (
                                <Badge variant="outline" className="text-[9px] py-0 h-4 bg-gray-100 text-gray-600 border-gray-300 font-bold uppercase tracking-wider">
                                    Archived
                                </Badge>
                            )}
                            {!c.author?.isActive && !c.author?.archived && (
                                <Badge variant="outline" className="text-[9px] py-0 h-4 bg-red-50 text-red-600 border-red-200 font-bold uppercase tracking-wider">
                                    Inactive
                                </Badge>
                            )}
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">
                                {new Date(c.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, {new Date(c.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                          <div className="text-sm text-gray-700 font-medium leading-relaxed whitespace-pre-wrap">
                             {renderCommentWithMentions(c.text)}
                          </div>
                       </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4 font-bold uppercase tracking-wider">No public comments yet</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Section - Comment Input */}
      {canPublicComment && (
        <div className="p-4 border-t border-border bg-white shadow-[0_-4px_12px_rgba(0,0,0,0.03)]">
          <div className="max-w-4xl mx-auto space-y-3">
            {canPrivateComment && (
                <div className="flex items-center gap-2 px-1">
                    <Checkbox 
                        id="internal-toggle" 
                        checked={isInternal} 
                        onCheckedChange={(checked) => setIsInternal(checked as boolean)}
                    />
                    <label 
                        htmlFor="internal-toggle" 
                        className="text-xs font-bold text-amber-700 cursor-pointer flex items-center gap-1 uppercase tracking-tight"
                    >
                        <Lock className="w-3 h-3" />
                        Private comment (visible only to team members)
                    </label>
                </div>
            )}
            <div className="relative">
              {showMentions && filteredMentions.length > 0 && (
                <div className="absolute bottom-full left-0 mb-2 w-64 bg-card border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto z-50">
                  {filteredMentions.map(user => (
                    <button
                      key={user.id}
                      onClick={() => handleSelectMention(user)}
                      className="w-full flex items-center gap-2 px-3 py-2 transition-colors text-left hover:bg-accent"
                    >
                      <div 
                        className="w-6 h-6 text-xs bg-green-500 rounded-full flex items-center justify-center text-white font-medium shrink-0" 
                        title={user.name}
                      >
                        {user.name?.split(' ').map((n: any) => n[0]).join('') || '?'}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{user.name}</div>
                        <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              <div className="flex gap-3 items-stretch">
                <textarea 
                  ref={inputRef}
                  rows={2}
                  placeholder={isInternal ? "Add a private note for the team..." : "Add a public comment..."} 
                  className={cn(
                    "flex-1 px-4 py-2.5 border rounded-xl focus:outline-none focus:ring-2 text-sm font-medium transition-all resize-none",
                    isInternal 
                        ? "bg-amber-50 border-amber-200 focus:ring-amber-500/20" 
                        : "bg-gray-50 border-gray-200 focus:ring-primary/20"
                  )}
                  value={comment}
                  onChange={handleCommentChange}
                  onKeyDown={(e) => {
                    // Enter inserts a new line; Ctrl/Cmd + Enter submits the comment
                    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                      e.preventDefault();
                      handleSendComment();
                    }
                  }}
                />
                <button 
                  disabled={!comment.trim() || isSending}
                  onClick={handleSendComment}
                  className={cn(
                    "px-6 py-2.5 rounded-xl hover:opacity-90 disabled:opacity-50 transition-all shadow-lg flex items-center justify-center min-w-[50px]",
                    isInternal ? "bg-amber-600 text-white shadow-amber-600/10" : "bg-primary text-primary-foreground shadow-primary/10"
                  )}
                >
                  {isSending ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> : <Send className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-[10px] text-gray-400 font-medium px-1">
                Press <span className="font-bold">Ctrl + Enter</span> to send your comment
              </p>
            </div>
          </div>
        </div>
      )}

      {!canPublicComment && !isRetired && (
          <div className="p-4 border-t border-border bg-gray-50 text-center text-xs text-gray-500 font-medium italic uppercase tracking-wider">
              You do not have permission to add comments to this request.
          </div>
      )}

      {/* Reassign Modal */}
      <Dialog open={isAssigneeModalOpen} onOpenChange={setIsAssigneeModalOpen}>
        <DialogContent className="max-w-md">
            <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                    <UserIcon className="w-5 h-5" />
                    Assign Team Members
                </DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-4">
                <p className="text-sm text-muted-foreground">Select one or more team members to handle this request.</p>
                <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                    {teamMembers.filter((m: any) => !m.user?.archived).length > 0 ? (
                        teamMembers.filter((m: any) => !m.user?.archived).map((member: any) => {
                            const isSelected = selectedAssignees.includes(member.user?.id?.toString());
                            return (
                                <button 
                                    key={member.id}
                                    onClick={() => toggleAssignee(member.user?.id?.toString())}
                                    className={cn(
                                        "w-full flex items-center justify-between p-3 rounded-xl border transition-all text-left",
                                        isSelected ? "bg-primary/5 border-primary shadow-sm" : "bg-white border-border hover:bg-gray-50"
                                    )}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className={cn(
                                            "w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-sm",
                                            isSelected ? "bg-primary" : "bg-gray-400"
                                        )}>
                                            {member.user?.name?.split(' ').map((n: any) => n[0]).join('')}
                                        </div>
                                        <div>
                                            <div className="text-sm font-bold text-gray-900">{member.user?.name}</div>
                                            <div className="text-xs text-muted-foreground uppercase font-bold tracking-tight">{member.role}</div>
                                        </div>
                                    </div>
                                    <Checkbox checked={isSelected} onCheckedChange={() => toggleAssignee(member.user?.id?.toString())} />
                                </button>
                            );
                        })
                    ) : (
                        <div className="text-center py-8 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                            <p className="text-sm text-gray-500 font-medium">No team members defined for this service desk.</p>
                        </div>
                    )}
                </div>
            </div>
            <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setIsAssigneeModalOpen(false)} disabled={isUpdatingAssignee}>
                    Cancel
                </Button>
                <Button onClick={handleSaveAssignees} disabled={isUpdatingAssignee} className="gap-2">
                    {isUpdatingAssignee && <Loader2 className="w-4 h-4 animate-spin" />}
                    Save Assignments
                </Button>
            </DialogFooter>
        </DialogContent>
      </Dialog>

      <ResolutionDetailsModal 
        isOpen={isResolutionModalOpen}
        onOpenChange={setIsResolutionModalOpen}
        onSubmit={handleResolutionSubmit}
        requesterName={request.requester?.name}
        isSubmitting={isUpdatingStatus}
      />
    </div>
  );
}
